// ArchiMate relationship tooltips with Magic Connector
import * as d3 from "d3";
import {Selection} from "d3";
import theme from "@semantical/theme.ts";
import {
    DiagramRelationshipElement,
    getStrokeDasharray
} from "@semantical/components/diagram-editor/DiagramRelationshipElement.tsx";
import type DiagramEditor from "@semantical/components/diagram-editor/DiagramEditor.tsx";
import React from "react";
import DiagramClass from "@semantical/modules/diagram/DiagramClass.ts";
import { RELATIONSHIP_TYPE } from "@semantical/modules/knowledge-graph";
import { ObjectProperty } from "@semantical/modules/ontology-model";

type TooltipDefinition = {
    id: string;
    label: string;
    appendTo: (svg: Selection<SVGGElement, any, any, any>) => Selection<any, any, any, any>;
    relationshipType: RELATIONSHIP_TYPE | "magic";
    isMagic?: boolean;
}

// Magic connector tooltip only
const svgTooltipDefinitions: TooltipDefinition[] = [
    // Magic connector - opens the radial picker
    {
        id: "tooltip-magic",
        label: "Connect",
        isMagic: true,
        appendTo(svg: d3.Selection<SVGGElement, any, any, any>) {
            const g = svg.append("g").style("cursor", "pointer");
            // Outer glow ring
            g.append("circle")
                .attr("cx", 10)
                .attr("cy", 0)
                .attr("r", 12)
                .attr("fill", "rgba(100, 200, 255, 0.2)")
                .attr("stroke", "rgba(100, 200, 255, 0.6)")
                .attr("stroke-width", 2);
            // Inner circle
            g.append("circle")
                .attr("cx", 10)
                .attr("cy", 0)
                .attr("r", 7)
                .attr("fill", "rgba(100, 200, 255, 0.8)")
                .attr("stroke", "#fff")
                .attr("stroke-width", 1);
            // Plus icon
            g.append("line")
                .attr("x1", 6).attr("y1", 0)
                .attr("x2", 14).attr("y2", 0)
                .attr("stroke", "#fff")
                .attr("stroke-width", 2)
                .attr("stroke-linecap", "round");
            g.append("line")
                .attr("x1", 10).attr("y1", -4)
                .attr("x2", 10).attr("y2", 4)
                .attr("stroke", "#fff")
                .attr("stroke-width", 2)
                .attr("stroke-linecap", "round");
            return g;
        },
        relationshipType: "magic"
    }
];

type Props = {
    diagramEditor: DiagramEditor,
    diagramClass: DiagramClass,
    width: number,
    addRelationship: (predicate: ObjectProperty, source: DiagramClass, target: DiagramClass) => void,
    onShowPicker?: (source: DiagramClass, target: DiagramClass, position: {x: number, y: number}) => void
};

type State = {
    pendingMagicTarget: DiagramClass | null;
    magicDropPosition: {x: number, y: number} | null;
};

export default class Tooltips extends React.Component<Props, State> {

    svg = React.createRef<SVGGElement>();

    state: State = {
        pendingMagicTarget: null,
        magicDropPosition: null
    };

    constructor(props: Props) {
        super(props);

        this.svg = React.createRef<SVGGElement>();
    }

    private tooltipsCreated = false;

    componentDidMount() {
        this.ensureTooltipsCreated();
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        // Recreate tooltips if diagramClass changed
        if (prevProps.diagramClass !== this.props.diagramClass) {
            this.tooltipsCreated = false;
        }
        this.ensureTooltipsCreated();
    }

    ensureTooltipsCreated() {
        if (this.tooltipsCreated || !this.svg.current) {
            return;
        }
        // Clear any existing content
        d3.select(this.svg.current).selectAll("*").remove();
        this.createTooltips();
        this.tooltipsCreated = true;
    }

    render() {
        // Position relative to parent DiagramClassElement (width + offset from right edge)
        return (
            <g ref={this.svg} className="tooltips" transform={`translate(${this.props.width + 20}, 0)`}>
            </g>
        );
    }

    handleDragStart = (_e: d3.D3DragEvent<any, any, any>, tooltipDefinition: TooltipDefinition) => {
        // For magic connector, use a neutral dashed line
        const isMagic = tooltipDefinition.isMagic;

        const line = d3.select(this.props.diagramEditor.svgViewport.current).append("path")
            .attr("id", "tooltipLine")
            .attr("stroke", isMagic ? "rgba(100, 200, 255, 0.8)" : theme.line)
            .attr("stroke-width", isMagic ? 2 : 1.5)
            .attr("fill", "none");

        if (isMagic) {
            line.attr("stroke-dasharray", "6,4");
        } else {
            // For non-magic connectors, use default open arrowhead at target
            // Specific markers come from ontology visual annotations on the predicate
            line.attr("marker-end", "url(#openArrowhead)");

            const dasharray = getStrokeDasharray(tooltipDefinition.relationshipType as RELATIONSHIP_TYPE);
            if (dasharray !== "none") {
                line.attr("stroke-dasharray", dasharray);
            }
        }
    }

    handleDrag = ({sourceEvent}: d3.D3DragEvent<any, any, any>) => {
        const diagramEditor = this.props.diagramEditor;
        const diagramClass = this.props.diagramClass;
        const targetClass = diagramEditor.findClassByTarget(sourceEvent.target);
        if (targetClass) {
            d3.select("#tooltipLine")
                .attr("d", DiagramRelationshipElement.GetPath(diagramClass, targetClass));
        } else {
            const pos = { x: diagramClass.x, y: diagramClass.y };
            const width = this.props.width;
            const [x,y] = d3.pointer(sourceEvent, diagramEditor.svgClasses.current);
            const straightLinePath = d3.line()([
                [pos.x + width, pos.y + 50],  // Start from right edge, middle height
                [x, y]
            ]);
            d3.select("#tooltipLine")
                .attr("d", straightLinePath);
        }
    }

    handleDragEnd = ({sourceEvent}: d3.D3DragEvent<any, any, any>, tooltipDefinition: TooltipDefinition) => {
        const diagramEditor = this.props.diagramEditor;
        const diagramClass = this.props.diagramClass;

        // Hide the line temporarily to find the element underneath
        const tooltipLine = d3.select("#tooltipLine");
        tooltipLine.style("pointer-events", "none").style("visibility", "hidden");

        // Use elementFromPoint to find what's actually under the cursor
        const elementUnderCursor = document.elementFromPoint(sourceEvent.clientX, sourceEvent.clientY);

        // Remove the line
        tooltipLine.remove();

        // Find the target class from the element under cursor
        const targetClass = elementUnderCursor ? diagramEditor.findClassByTarget(elementUnderCursor as HTMLElement) : undefined;

        if (targetClass) {
            if (tooltipDefinition.isMagic) {
                // Magic connector: show the relationship picker
                const [x, y] = d3.pointer(sourceEvent, diagramEditor.svgViewport.current);
                if (this.props.onShowPicker) {
                    this.props.onShowPicker(
                        diagramClass,
                        targetClass,
                        { x, y }
                    );
                }
            } else {
                // Direct relationship type - create a simple ObjectProperty
                const relType = tooltipDefinition.relationshipType as RELATIONSHIP_TYPE;
                const predicate: ObjectProperty = {
                    uri: `urn:objectproperty:${relType}`,
                    label: tooltipDefinition.label,
                    relationshipType: relType
                };
                this.props.addRelationship(
                    predicate,
                    diagramClass,
                    targetClass
                );
            }
        }
    }

    createTooltips() {
        if (!this.svg.current) {
            console.error("Cannot create tooltips: svg ref is null");
            return;
        }

        let dy = 5;

        svgTooltipDefinitions.forEach((tooltipDefinition) => {
            const svgTooltip = tooltipDefinition.appendTo(d3.select(this.svg.current!))
                .attr("transform", `translate(0,${dy})`);

            // Magic connector gets more vertical space
            dy += tooltipDefinition.isMagic ? 30 : 20;

            const dragBehavior = d3.drag<any, unknown>()
                .on("start", (e) => this.handleDragStart(e, tooltipDefinition))
                .on("drag", (e) => this.handleDrag(e))
                .on("end", (e) => this.handleDragEnd(e, tooltipDefinition));

            svgTooltip.call(dragBehavior);
        });
    }
}


